import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Clock, AlertCircle, Trophy, Check, X, Loader } from "lucide-react";

export default function MockExam() {
  const { examType, discipline } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [examStarted, setExamStarted] = useState(false);
  const [examFinished, setExamFinished] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [questionCount, setQuestionCount] = useState(20);
  const [result, setResult] = useState(null);
  const timerRef = useRef(null);

  const getExamTime = (count) => {
    if (count <= 20) return 30;
    if (count <= 50) return 90;
    return 180;
  };

  useEffect(() => {
    fetchQuestions(questionCount);
  }, [questionCount]);

  useEffect(() => {
    if (examStarted && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [examStarted]);

  const fetchQuestions = async (count) => {
    setLoading(true);
    try {
      const res = await api.get("/mock-exam/questions", {
        params: { exam_type: examType, discipline, count }
      });
      setQuestions(res.data);
    } catch (err) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const startExam = () => {
    setExamStarted(true);
    setTimeLeft(getExamTime(questions.length) * 60);
  };

  const handleAnswer = (answer) => {
    const question = questions[currentIndex];
    setAnswers(prev => ({ ...prev, [question.question_id]: answer }));
  };

  const handleSubmit = async () => {
    clearInterval(timerRef.current);
    
    const answeredQuestions = questions.map(q => ({
      question_id: q.question_id,
      selected_answer: answers[q.question_id] || null,
      correct_answer: q.correct_answer,
      is_correct: answers[q.question_id] === q.correct_answer
    }));

    const correctCount = answeredQuestions.filter(a => a.is_correct).length;
    const timeTaken = getExamTime(questions.length) * 60 - timeLeft;

    try {
      const res = await api.post("/mock-exam/submit", {
        exam_type: examType,
        discipline,
        total_questions: questions.length,
        correct_answers: correctCount,
        time_taken_seconds: timeTaken,
        answers: answeredQuestions
      });
      
      setResult({
        ...res.data,
        total_questions: questions.length,
        correct_answers: correctCount,
        answers: answeredQuestions
      });
      setExamFinished(true);
    } catch (err) {
      toast.error("Failed to submit exam");
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const answeredCount = Object.keys(answers).length;

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Start screen
  if (!examStarted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="btn-ghost p-2">
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-slate-900">Mock Exam</h1>
          </div>
        </header>

        <main className="max-w-lg mx-auto px-4 py-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-blue-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {examType} Mock Exam
          </h2>
          <p className="text-slate-600 mb-8">{discipline.replace(/_/g, " ")}</p>

          <div className="card mb-6">
            <p className="text-sm text-slate-500 mb-4">Select number of questions:</p>
            <div className="flex justify-center gap-3">
              {[20, 50, 100].map(count => (
                <button
                  key={count}
                  data-testid={`count-${count}`}
                  onClick={() => setQuestionCount(count)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                    questionCount === count
                      ? "bg-blue-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {count}
                </button>
              ))}
            </div>
            
            <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-2 gap-4 text-left">
              <div>
                <p className="text-sm text-slate-500">Questions</p>
                <p className="text-2xl font-bold text-slate-900">{questions.length}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Time Limit</p>
                <p className="text-2xl font-bold text-slate-900">{getExamTime(questions.length)} min</p>
              </div>
            </div>
          </div>

          <div className="card bg-amber-50 border-amber-200 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <p className="text-sm text-amber-800 text-left">
                Timer cannot be paused once started. Make sure you have uninterrupted time.
              </p>
            </div>
          </div>

          <button
            data-testid="start-exam-btn"
            onClick={startExam}
            className="btn-primary text-lg px-12"
          >
            Start Exam
          </button>
        </main>
      </div>
    );
  }

  // Results screen
  if (examFinished && result) {
    const percentage = Math.round(result.score_percentage);
    const passed = percentage >= 70;

    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <h1 className="text-xl font-bold text-slate-900">Exam Results</h1>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12">
          <div className="card text-center mb-8">
            <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
              passed ? "bg-green-100" : "bg-red-100"
            }`}>
              <Trophy className={`w-10 h-10 ${passed ? "text-green-600" : "text-red-600"}`} />
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {passed ? "Congratulations!" : "Keep Practicing!"}
            </h2>
            <p className="text-slate-600 mb-6">
              {passed ? "You passed the mock exam!" : "You need 70% to pass. Review and try again."}
            </p>

            <div className="text-5xl font-bold text-slate-900 mb-2">
              {percentage}%
            </div>
            <p className="text-slate-500">
              {result.correct_answers}/{result.total_questions} correct
            </p>
          </div>

          {/* Review answers */}
          <div className="card mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">Review Answers</h3>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {result.answers.map((ans, i) => (
                <div key={i} className={`p-3 rounded-lg flex items-center gap-3 ${
                  ans.is_correct ? "bg-green-50" : "bg-red-50"
                }`}>
                  {ans.is_correct ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <X className="w-5 h-5 text-red-600" />
                  )}
                  <span className="text-sm text-slate-700">
                    Q{i + 1}: {ans.selected_answer || "Skipped"} 
                    {!ans.is_correct && ` (Correct: ${ans.correct_answer})`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => navigate(`/practice/${examType}/${discipline}`)}
              className="btn-secondary flex-1"
            >
              Practice More
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="btn-primary flex-1"
            >
              Back to Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Exam in progress
  const question = questions[currentIndex];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header with timer */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Question {currentIndex + 1} of {questions.length}</p>
              <p className="text-xs text-slate-400">{answeredCount} answered</p>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
              timeLeft < 300 ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
            }`}>
              <Clock size={18} />
              <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
            </div>
          </div>
          <div className="mt-3 progress-bar">
            <div className="progress-bar-fill" style={{ width: `${(answeredCount / questions.length) * 100}%` }} />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Question navigator */}
        <div className="card mb-6">
          <p className="text-sm text-slate-500 mb-3">Jump to question:</p>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {questions.map((q, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                  i === currentIndex
                    ? "bg-blue-600 text-white"
                    : answers[q.question_id]
                    ? "bg-green-100 text-green-700"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Question */}
        <div className="question-card mb-6">
          <span className="px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium">
            {question.topic}
          </span>
          <h2 className="text-lg font-semibold text-slate-900 mt-4 mb-6">
            {question.question_text}
          </h2>

          <div className="space-y-3">
            {Object.entries(question.options).map(([key, value]) => (
              <button
                key={key}
                onClick={() => handleAnswer(key)}
                className={`option-btn ${answers[question.question_id] === key ? "selected" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center font-semibold">
                    {key}
                  </span>
                  <span>{value}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
            disabled={currentIndex === 0}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            <ArrowLeft size={18} /> Previous
          </button>

          {currentIndex === questions.length - 1 ? (
            <button
              data-testid="submit-exam-btn"
              onClick={handleSubmit}
              className="btn-primary flex items-center gap-2"
            >
              Submit Exam
            </button>
          ) : (
            <button
              onClick={() => setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1))}
              className="btn-primary flex items-center gap-2"
            >
              Next <ArrowRight size={18} />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
